import { SimpleTooltip } from "@/components/Common/Tooltip/SimpleTooltip";
import { IconProps } from "@/components/Icons";
import { cn } from "@/lib/utils";

type OptionType = {
  label: React.ReactNode;
  value: string;
  icon?: React.FC<IconProps>;
  customIcon?: React.FC;
  disabled?: boolean;
  description?: string | React.ReactNode;
  component?: React.ReactNode;
  customRadio?: React.ReactNode;
  className?: string;
  isLabelUnderneath?: boolean;
  descriptionClassName?: string;
  tooltip?: string;
};

export type RadioCardInputProps = {
  value?: string;
  options: OptionType[];
  disabled?: boolean;
  name: string;
  onChange: (value: string) => void;
  onSelect?: (value: string) => void;
  className?: string;
  baseHOCClassName?: string;
};

type OptionComponentProps = {
  option: OptionType;
  isActive: boolean;
  onClick: () => void;
};

export default function RadioCardInput({
  onChange,
  onSelect,
  value = "",
  options,
  className
}: RadioCardInputProps) {
  return (
    <div className={`grid gap-2 lg:grid-cols-2 ${className}`}>
      {options.map((option) => {
        const isActive = option.value === value;
        return option.tooltip ? (
          <SimpleTooltip
            id={`option-tooltip-${option.value}`}
            content={
              <div className="subtext-small text-foreground">{option.tooltip}</div>
            }
            tooltipClassName="!max-w-[300px] whitespace-normal"
            place="top"
            usePortal
            key={`option-key-${option.value}`}
            className="min-w-0"
          >
            <OptionComponent
              key={option.value}
              option={option}
              isActive={isActive}
              onClick={() => {
                if (option.disabled) {
                  return;
                }

                onChange(option.value);
                onSelect?.(option.value);
              }}
            />
          </SimpleTooltip>
        ) : (
          <OptionComponent
            key={option.value}
            option={option}
            isActive={isActive}
            onClick={() => {
              if (option.disabled) {
                return;
              }

              onChange(option.value);
              onSelect?.(option.value);
            }}
          />
        );
      })}
    </div>
  );
}

const OptionComponent = ({ option, isActive, onClick }: OptionComponentProps) => {
  return (
    <div
      key={option.value}
      onClick={onClick}
      className={`
            ${isActive ? "border-gray-dark-1300" : "border-gray-800"}
            ${option.disabled ? "opacity-50" : "cursor-pointer hover:border-gray-dark-1300"}
            rounded border bg-gray-dark-1700 p-6 ${option.className}
          `}
    >
      <div className={`flex flex-1 items-center justify-between`}>
        {option.icon && (
          <div className="mr-4 h-[32px] w-[32px] rounded bg-gradient-to-b from-border to-border p-[1px] dark:from-[#909090] dark:to-[#2A2A2A]">
            <div className="flex h-full items-center justify-center rounded bg-background">
              <option.icon className="h-[20px] w-[20px]" />
            </div>
          </div>
        )}
        {option.customIcon && <option.customIcon />}
        {!option.isLabelUnderneath && (
          <div className="mr-4 flex-1">
            <div className="subtext-small lg:subtext-medium text-black dark:text-foreground">
              {option.label}
            </div>
          </div>
        )}
        {option.customRadio ? (
          option.customRadio
        ) : (
          <div
            className={`radio flex h-[24px] w-[24px] items-center justify-center rounded-full border ${isActive ? "border-gray-dark-700" : "border-gray-dark-700"}`}
          >
            {isActive && (
              <div className="h-[10px] w-[10px] rounded-full bg-black dark:bg-gray-light-1000"></div>
            )}
          </div>
        )}
      </div>
      {option.isLabelUnderneath && (
        <div className="mt-2 flex-1">
          <div className="subtext-small lg:subtext-medium">{option.label}</div>
        </div>
      )}
      {option.description ? (
        <div
          className={cn(
            `subtext-x-small mt-2 text-gray ${option.customIcon || option.icon ? "lg:ml-12" : ""}`,
            option.descriptionClassName
          )}
        >
          {option.description}
        </div>
      ) : null}
      {option.component}
    </div>
  );
};
